import styled from "styled-components";
import React, { useState } from "react";
import { ACSS, Small } from "@upsolve/ui";
import upsolveStats from "../../../content/onBuild/upsolveStats.json";
import { MyUpsolveButtonLink } from "../Links/MyUpsolveLink";
import trackComponentAction from "../analytics/trackComponentAction";
import trackComponentImpression from "../analytics/trackComponentImpression";
import withViewportWatch from "../analytics/withViewportWatch";
import { CTATarget, getCTARenderedCopy, getCTATrackingTarget, getCTAURL } from "./getCTAData";
import ImageInContext from "../Media/ImageInContext";
import useModal from "../modals/useModal";
import { createPortal } from "react-dom";

const entityToData: { [key in CTATarget]: { src: string; alt: string } } = {
  [CTATarget.SCREENER]: { src: "/images/product-demo-short.gif", alt: "Upsolve app demo" },
  [CTATarget.STUDENT_LOANS]: { src: "/images/StudentLoanCTAGraphic.gif", alt: "Student Loans Demo" },
};

export const CTAHeroGraphic = ({ target = CTATarget.SCREENER }: { target?: CTATarget }) => {
  const { src, alt } = entityToData[target];
  if (src) {
    return <ImageInContext alt={alt} src={src} />;
  } else {
    return null;
  }
};

const trackingProps = {
  componentName: "HeroFilerCta",
  componentVersion: "9",
};

type Props = {
  isInViewport: boolean;
  trackingPageContext: any;
  stopWatcher: () => void;
  target?: CTATarget;
};

/**
 * HeroFilerCta
 * - v0: Defaults
 * - v1: Geolocation on title: "We help {regionName} residents like you file bankruptcy for free." (reverted, poorer performance)
 * - v2: Replaced value prop bullets with image
 * - v3: Replaced value prop image with GIF
 * - v4: Removed fixed header nav. Added "online now" text under CTA buttton
 * - v5: Incorporated reading bankruptcy myth busters. Cut out copy
 * - v6: Put back nonprofit word at start
 * - v7: "Ask your questions, get expert-written content, explore our free bankruptcy filing tool" instead of "Upsolve's nonprofit tool helps you file bankruptcy for free"
 * - v8: Put back just the mobile app. Seemed to do better cause I get people realized we had a tool
 * - v9: Incorporated Modal support
 * @component
 * @version 8
 */
const HeroFilerCta = (props: Props) => {
  const [impressionTracked, setImpressionTracked] = useState(false);
  const ctaTarget = props.target || CTATarget.SCREENER;
  const modal = useModal();

  const ctaUrl = getCTAURL(ctaTarget);

  const ctaCopy = getCTARenderedCopy(ctaTarget);
  const ctaTrackingTarget = getCTATrackingTarget(ctaTarget);

  // Impression Tracking
  if (props.isInViewport && !impressionTracked) {
    setImpressionTracked(true);
    props.stopWatcher();
    trackComponentImpression({
      ...trackingProps,
      pageContext: props.trackingPageContext,
    });
  }

  const CTAModal: { [key in CTATarget]: { modal: any } } = {
    [CTATarget.SCREENER]: { modal: null },
    [CTATarget.STUDENT_LOANS]: { modal: null },
  };

  const handleClick = () => {
    trackComponentAction({
      ...trackingProps,
      actionId: `heroFilerCta.${ctaTrackingTarget}`,
      actionMethod: "click",
      pageContext: props.trackingPageContext,
    });

    const targetUrl = getCTAURL(ctaTarget);

    if (CTAModal[ctaTarget] && CTAModal[ctaTarget].modal) {
      // Open modal
      modal.setIsOpen(true, CTAModal[ctaTarget].modal);
    } else if (targetUrl) {
      // Navigate to href
      window.location.href = targetUrl;
    }
  };

  return (
    <StyledHeroFilerCta>
      <div className="img-value-props">
        <CTAHeroGraphic target={ctaTarget} />
      </div>
      <div className="headline">{ctaCopy.title}</div>
      <div className="action">
        <MyUpsolveButtonLink aria-busy="false" size="lg" href={ctaUrl} onClick={handleClick}>
          {ctaCopy.buttonText}
        </MyUpsolveButtonLink>
        {ctaTarget === CTATarget.SCREENER ? (
          <div className="num-starts">
            <svg>
              <circle cx="5" cy="5" r="5" />
            </svg>
            <Small>
              <b>
                {upsolveStats.active.total == null ? "" : Number(upsolveStats.active.total).toLocaleString()} people
                online filing
              </b>
            </Small>
          </div>
        ) : null}
      </div>
      {modal.isOpen && createPortal(modal.component, document.body)}
    </StyledHeroFilerCta>
  );
};

const StyledHeroFilerCta = styled.div`
  box-shadow: 0 5px 15px -5px rgba(0, 0, 0, 0.1);
  padding: 0 2em 2em;
  background: ${(props) => props.theme.colors.white["900"]};
  max-height: 80vh;
  overflow: hidden;
  div.headline {
    color: ${(props) => props.theme.colors.black["100"]};
    font-size: 28px;
    font-weight: 500;
    line-height: 130%;
    text-align: center;
    @media (max-height: 660px) {
      font-size: 22px;
    }
  }
  div.action {
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: center;
    a,
    a:hover {
      background: linear-gradient(270deg, #ff863b -65.78%, #f66161 45.1%);
      box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
      border: none;
      border-bottom: 4px solid #d13f3f;
      border-radius: 6px;
      margin: 0.5em 0 0;
      width: 100%;
      font-weight: 500;
    }
    p {
      ${ACSS}
      text-decoration: underline;
      &:hover {
        cursor: pointer;
      }
    }
  }
  @keyframes oscillate-online-circle {
    0% {
      fill: ${(props) => props.theme.colors.green[500]};
    }
    100% {
      fill: ${(props) => props.theme.colors.green[100]};
    }
  }
  div.num-starts {
    text-align: center;
    margin: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 36px;
    small {
      color: ${(props) => props.theme.colors.gray[700]};
    }
    svg {
      height: 14px;
      width: 14px;
      margin: 6px 2px 0 0;
      circle {
        height: 100%;
        width: 100%;
        animation: oscillate-online-circle 0.75s alternate infinite;
      }
    }
  }
  div.img-value-props {
    padding: 1em 0 0;
    @media (max-height: 660px) {
      padding: 0;
    }
    max-width: 320px;
    margin: 0 auto;
    video {
      height: 100%;
      width: 100%;
      max-width: 400px;
      max-height: 400px;
      margin: 0 auto;
      border-bottom: 1px solid ${(props) => props.theme.colors.white[500]};
    }
  }

  @media (max-width: 45em) {
    padding: 2em;
    margin-left: -0.5em;
    margin-right: -0.5em;
    box-shadow: 0 0 15px -5px rgba(0, 0, 0, 0.15);
    div.headline {
      font-size: 24px;
    }
  }
`;

export default withViewportWatch(HeroFilerCta);
